import type { CardDef, Rarity } from '../types.js';

export type CardgenJobStatus = 'pending' | 'generating' | 'ready' | 'failed';

export type CardgenProgressEvent =
  | { type: 'card_start'; index: number; rarity: Rarity; brief: string }
  | { type: 'card_name'; index: number; name: string; description: string }
  | { type: 'card_stats'; index: number; score?: number; inBand?: boolean; attempt?: number }
  | { type: 'card_art'; index: number; variants: number }
  | { type: 'card_done'; index: number; cardId: string }
  | { type: 'debug'; index: number; stage: string; prompt?: string; response?: string; meta?: Record<string, unknown> }
  | { type: 'job_complete' }
  | { type: 'job_failed'; error: string };

export interface CardgenJob {
  id: string;
  status: CardgenJobStatus;
  progress: CardgenProgressEvent[];
  cards?: CardDef[];
  error?: string;
}

export interface CardgenJobRequest {
  cardsPerPack: number;
  rarityWeights: Record<Rarity, number>;
  /** Applied to every card in the job. Dev tool uses this to pin archetype/culture. */
  overrides?: CardgenOverride;
  /** Optional free-text player hint. Server uses the LLM to extract a culture OR
   *  archetype override (randomly keeping one if both are identified). */
  hint?: string;
  /** Admin dev tool flag: run the full prompt pipeline but skip the fal.ai image
   *  generation step (the most expensive API call) to iterate on text prompts
   *  cheaply. When true, the resulting card has `art: ''`. */
  skipImages?: boolean;
}

export interface CardgenOverride {
  rarity?: Rarity;
  /** Seed the no-hint template path — admin dev tool uses this to pin every
   *  card in the pack to e.g. "a warrior from Norse culture". Ignored when a
   *  player hint is supplied (the judge takes over and generates briefs). */
  archetype?: string;
  culture?: string;
}

export interface CardgenJobResponse {
  jobId: string;
}

export interface DiscountConfig {
  /** Integer percentage off the base cost, 1-99. */
  percentOff: number;
  /** Label rendered on the banner in the shop UI. */
  label: string;
}

export interface CustomPackConfig {
  entryCost: number;
  keepCosts: Record<string, number>;
  cardsPerPack: number;
  rarityWeights: Record<Rarity, number>;
  /** Optional promo discount. Omit or delete the key to end the promo. */
  discount?: DiscountConfig;
}

/** Ticket id that, when owned, lets the player open a custom pack for
 *  free (no gems spent). Awarded by tournament prize tiers and surfaced
 *  on the custom-pack tile with the same FREE banner the regular
 *  basic / premium packs use. Mirrors the inventory model in
 *  `data/packs.json` (basic → `free-basic-pack`, premium →
 *  `free-premium-pack`); the custom pack lives outside `packs.json`
 *  since its rarity/cost flow is bespoke, so the slug is exposed here
 *  for both client and server to import. */
export const CUSTOM_PACK_TICKET_ID = 'free-custom-pack';

/** Effective gem cost a player pays to buy a custom pack, after any active
 *  discount. The server and all client price displays route through here so
 *  they can never diverge. */
export function getEffectiveEntryCost(config: CustomPackConfig): number {
  const d = config.discount;
  if (!d || !(d.percentOff > 0 && d.percentOff < 100)) return config.entryCost;
  return Math.max(0, Math.round(config.entryCost * (1 - d.percentOff / 100)));
}

/** Sanitize a player-provided hint before substituting into an LLM prompt.
 *  Keeps Unicode letters/digits + basic punctuation; strips brackets, quotes,
 *  backticks, control chars, and anything else. Collapses whitespace, caps at
 *  100 chars. Returns '' for hint-free jobs. */
export function sanitizeHint(raw: string | undefined): string {
  if (typeof raw !== 'string') return '';
  // Replace everything outside the whitelist with a single space so that
  // words separated by a stripped character don't fuse into one token.
  const filtered = raw.replace(/[^\p{L}\p{N}\s.,!?'\-]/gu, ' ');
  return filtered.replace(/\s+/g, ' ').trim().slice(0, 100);
}
